import { defineStore } from "pinia";
import type { AuthedUserModel } from "@/models/authedUser.model";
import type { Router } from "vue-router";
import type {
  UserLoginResponse,
  UserRefreshAccessTokenRequest,
} from "@/functional/api/apiUsers";
import apiUsers from "@/functional/api/apiUsers";
import parseJwt from "@/functional/auth/parseJwt";
import isJwtExpired from "@/functional/auth/isJwtExpired";
import { setSentryUser } from "@/setupSentry";
import { useToast } from "vue-toast-notification";
import type { OrganisationUserModel } from "@/models/organisationUser.model";
import { RouteName } from "@/router/routeName.ts";
import apiMfa from "@/functional/api/apiMfa.ts";
import apiRecoveryCode, {
  type RecoveryCodeLoginRequest,
} from "@/functional/api/apiRecoveryCode.ts";
import userRequiresMfaSignIn from "@/functional/userRequiresMfaSignIn.ts";
import { computed, ref } from "vue";
import type { IUser } from "configcat-vue";

function isUserLoginResponse(obj: unknown): obj is UserLoginResponse {
  if (typeof obj !== "object" || obj === null) {
    return false;
  }

  const userObj = obj as UserLoginResponse;

  return (
    typeof userObj.publicId === "string" &&
    typeof userObj.firstName === "string" &&
    typeof userObj.lastName === "string" &&
    (typeof userObj.imageUrl === "string" || userObj.imageUrl === null) &&
    typeof userObj?.organisation?.publicId === "string" &&
    typeof userObj?.organisation?.name === "string" &&
    typeof userObj.organisation?.createdAt === "string" &&
    (typeof userObj?.organisation?.modifiedAt === "string" ||
      userObj?.organisation?.modifiedAt === null) &&
    typeof userObj.createdAt === "string" &&
    (typeof userObj.modifiedAt === "string" || userObj.modifiedAt === null) &&
    typeof userObj.email === "string" &&
    typeof userObj.emailConfirmed === "boolean" &&
    (typeof userObj.organisationUserName === "string" ||
      userObj.organisationUserName === null) &&
    typeof userObj.jwtToken === "string" &&
    typeof userObj.userRefreshToken === "object" &&
    userObj.userRefreshToken !== null &&
    typeof userObj.userRefreshToken.token === "string"
  );
}

export interface AuthState {
  returnToUrl: string | null;
  currentUser: AuthedUserModel | null;
  currentUserPermissions: string[];
  queue: Array<() => Promise<unknown>>;
  isProcessing: boolean;
}

const localStorageAuthedUserSlug = "user";
const toast = useToast();

export const useAuthStore = defineStore("auth", () => {
  // State
  const returnToUrl = ref<string | null>(null);
  const currentUser = ref<AuthedUserModel | null>(null);
  const currentUserPermissions = ref<string[]>([]);
  const queue = ref<Array<() => Promise<unknown>>>([]);
  const isProcessing = ref<boolean>(false);

  // Getters
  const isAuthenticated = computed(() => currentUser.value !== null);
  const user = computed(() => currentUser.value);
  const permissions = computed(() => currentUserPermissions.value);
  const configCatUser = computed((): IUser | null => {
    if (currentUser.value === null) {
      return null;
    }
    return {
      identifier: currentUser.value.publicId,
      custom: {
        organisationId: currentUser.value.organisation.publicId,
      },
    };
  });

  // Actions
  const processQueue = async (): Promise<void> => {
    if (queue.value.length === 0 || isProcessing.value) {
      return;
    }

    isProcessing.value = true;

    while (queue.value.length > 0) {
      const action = queue.value.shift();
      if (action) {
        await action();
      }
    }
    isProcessing.value = false;
  };

  const addToQueue = (action: () => Promise<unknown>): Promise<void> => {
    queue.value.push(action);
    return processQueue();
  };

  const reset = (): void => {
    returnToUrl.value = null;
    currentUser.value = null;
    setSentryUser(null);
    currentUserPermissions.value = [];
    localStorage.removeItem(localStorageAuthedUserSlug);
  };

  const hydrateCurrentUserFromStorage = async (): Promise<void> => {
    const storedUserStr = localStorage.getItem(localStorageAuthedUserSlug);

    if (storedUserStr) {
      try {
        const storedUser = JSON.parse(storedUserStr);

        if (isUserLoginResponse(storedUser)) {
          currentUser.value = storedUser;
          setSentryUser(currentUser.value);

          const token = storedUser.jwtToken;
          if (!token) {
            throw new Error("JWT token is missing");
          }

          const parsedToken = parseJwt(token);
          if (!parsedToken) {
            throw new Error("Failed to parse JWT token");
          }

          currentUserPermissions.value = parsedToken?.permission ?? [];

          if (isJwtExpired(parsedToken)) {
            console.warn("Token expired attempting refresh");
            await refreshToken();
          }
        } else {
          reset();
          throw new Error("Invalid user object in localStorage");
        }
      } catch (error) {
        console.error("Caught error during hydrating", error);
        reset();
        throw new Error("Failed to hydrate current user from storage.");
      }
    }
  };

  const setUserFromLoginResponse = async (
    user: UserLoginResponse,
  ): Promise<AuthedUserModel> => {
    if (!isUserLoginResponse(user)) {
      reset();
      throw new Error("Invalid UserLoginResponse object");
    }

    try {
      currentUser.value = user;
      setSentryUser(currentUser.value);
      localStorage.setItem(localStorageAuthedUserSlug, JSON.stringify(user));

      const token = user.jwtToken;

      if (!token) {
        throw new Error("JWT token is missing");
      }

      const parsedToken = parseJwt(token);
      if (!parsedToken) {
        throw new Error("Failed to parse JWT token");
      }

      currentUserPermissions.value = parsedToken?.permission ?? [];

      return currentUser.value;
    } catch (error) {
      console.debug(error);
      reset();
      throw new Error("Failed to set user from login response");
    }
  };

  const checkIfOrgContractExpired = async (
    user: UserLoginResponse,
  ): Promise<void> => {
    if (!user) return;

    if (user.organisation.organisationStatus.toUpperCase() !== "ACTIVE") {
      toast.warning(
        `Your organisation is not active. Please contact your organisation administrator to activate your subscription.`,
        { position: "top-right", duration: 360000 },
      );
      return;
    }
  };

  const refreshToken = async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      addToQueue(async () => {
        if (currentUser.value === null) {
          console.error("Cannot refresh tokens for unset user");
          reject();
          return;
        }

        const refreshTokenBody = {
          refreshToken: currentUser.value.userRefreshToken.token,
          userPublicId: currentUser.value.publicId,
        } as UserRefreshAccessTokenRequest;

        try {
          const user = await apiUsers.refreshAccessToken(refreshTokenBody);
          await setUserFromLoginResponse(user);
          console.log("Refreshed token");
          resolve();
        } catch (e) {
          reset();
          console.error("Failed to refresh tokens", e);
          reject();
        }
      });
    });
  };

  const login = async (
    email: string,
    password: string,
    router: Router,
  ): Promise<AuthedUserModel> => {
    const user: UserLoginResponse = await apiUsers.login({
      email: email,
      password,
    });
    await setUserFromLoginResponse(user);
    console.log("Mid-login permissions:", permissions.value);
    if (userRequiresMfaSignIn(permissions.value)) {
      console.log("Logging in and must use passwordless credential");
      await router.push({
        name: RouteName.MFA,
        params: {},
      });

      return user;
    }

    await router.push(returnToUrl.value || "/");
    await checkIfOrgContractExpired(user);

    return user;
  };

  const passwordlessLogin = async (
    email: string,
    userPublicId: string,
    router: Router,
  ): Promise<AuthedUserModel> => {
    const userResponse = await apiMfa.passKeySignIn(userPublicId, email);
    if (userResponse.error !== undefined) {
      console.log(
        "There was an error logging in with passwordless",
        userResponse,
      );
      throw Error(userResponse.error.title);
    }

    const user = userResponse as UserLoginResponse;
    await setUserFromLoginResponse(user);
    console.log("Mid-login permissions:", permissions.value);

    await router.push(returnToUrl.value || "/");
    await checkIfOrgContractExpired(user);

    return user;
  };

  const recoveryCodeLogin = async (
    email: string,
    recoveryCode: string,
    router: Router,
  ): Promise<AuthedUserModel> => {
    console.log("Recovery code login...");
    const loginDetails: RecoveryCodeLoginRequest = {
      email: email,
      recoveryCode: recoveryCode,
    };
    const user = await apiRecoveryCode.login(loginDetails);

    await setUserFromLoginResponse(user);
    console.log("Mid-login permissions:", permissions.value);
    if (userRequiresMfaSignIn(permissions.value)) {
      console.log("Logging in and must use a recovery code");
      await router.push({
        name: RouteName.MFA,
        params: {},
      });

      return user;
    }

    await router.push(returnToUrl.value || "/");
    await checkIfOrgContractExpired(user);

    return user;
  };

  const organisationUserLogin = async (
    orgId: string,
    emailOrOrganisationUserName: string,
    password: string,
    router: Router,
  ): Promise<AuthedUserModel> => {
    const user = await apiUsers.organisationLogin({
      organisationPublicId: orgId,
      emailOrOrganisationUserName,
      password,
    });
    await setUserFromLoginResponse(user);
    await router.push(returnToUrl.value || "/");

    return user;
  };

  const updatePassword = async (
    token: string,
    singleUseCode: string,
    newPassword: string,
    router: Router,
  ): Promise<AuthedUserModel> => {
    console.log("Update password...");
    const parsedToken = parseJwt(token);
    const userId = parsedToken?.["user-id"];
    console.log("userId:", userId);
    if (userId === undefined) {
      throw new Error("User Id not set");
    }
    const user = await apiUsers.updatePassword(
      userId,
      token,
      singleUseCode,
      newPassword,
    );
    await setUserFromLoginResponse(user);

    if (userRequiresMfaSignIn(permissions.value)) {
      console.log("Logging in and must use passwordless credential");
      await router.push({
        name: RouteName.MFA,
        params: {},
      });

      return user;
    }

    await router.push(returnToUrl.value || "/");

    return user;
  };

  const updateCurrentUser = (user: AuthedUserModel): void => {
    currentUser.value = user;
    setSentryUser(currentUser.value);
    localStorage.setItem(localStorageAuthedUserSlug, JSON.stringify(user));
  };

  const updateCurrentUserAttributes = (user: OrganisationUserModel): void => {
    if (
      user.publicId !== currentUser.value?.publicId ||
      currentUser.value === null
    ) {
      console.warn(
        `Not updating current user attributes due to an ID mismatch. Previously: ${currentUser.value?.publicId}, Updating to: ${user.publicId}`,
      );
      return;
    }
    console.log("Updating current user attributes");
    const newCurrentUser = {
      ...user,
      ...currentUser.value,
    } as UserLoginResponse;

    updateCurrentUser(newCurrentUser);
  };

  const revokeRefreshToken = async (): Promise<void> => {
    try {
      await apiUsers.revokeRefreshToken();
    } catch (error) {
      console.error("An error occurred while revoking refresh token: ", error);
    }
  };

  const logout = async (router: Router): Promise<void> => {
    await revokeRefreshToken();
    reset();
    await router.push("/login");
  };

  return {
    // State
    returnToUrl,
    currentUser,
    currentUserPermissions,
    queue,
    isProcessing,

    // Getters
    configCatUser,
    isAuthenticated,
    user,
    permissions,

    // Actions
    processQueue,
    addToQueue,
    hydrateCurrentUserFromStorage,
    login,
    passwordlessLogin,
    recoveryCodeLogin,
    organisationUserLogin,
    setUserFromLoginResponse,
    updatePassword,
    updateCurrentUser,
    updateCurrentUserAttributes,
    checkIfOrgContractExpired,
    refreshToken,
    revokeRefreshToken,
    reset,
    logout,
  };
});
